import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useParams } from "react-router-dom";

import VersionSelector, { VersionSelectorProps } from "../../VersionSelector";
import { IWorkflow } from "../WorkflowCanvas/workflow-store";

import { useArchiveWorkflowVersion, useRequestWorflowCounts } from "@/queries/workflows";

export interface WorkflowVersionSelectorProps extends VersionSelectorProps {
  workflow: IWorkflow;
  refreshVersions: () => void;
}

const WorkflowVersionSelector = (props: WorkflowVersionSelectorProps) => {
  const { workflow, refreshVersions, ...selectorProps } = props;
  const { workspaceId } = useParams();
  const queryClient = useQueryClient();

  const archiveWorkflowVersionMutation = useArchiveWorkflowVersion();

  const invalidateChangelogQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["global-changelog", workspaceId],
    });
    queryClient.invalidateQueries({ queryKey: ["changelog"] });
    queryClient.invalidateQueries({
      queryKey: ["changelog-pending-count", workspaceId],
    });
  }, [queryClient, workspaceId]);

  const onRemoveReleaseLabel = useCallback(
    async (labelId: number) => {
      try {
        await workflow.removeReleaseLabel(labelId);
        refreshVersions();
        invalidateChangelogQueries();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error((e as string) ?? "Error handling remove release label");
      }
    },
    [invalidateChangelogQueries, refreshVersions, workflow],
  );

  const onAddReleaseLabel = async (labelName: string, versionId: number | string) => {
    const nextVersionId = Number(versionId) || workflow.version_id!;
    const response = await workflow.addReleaseLabel(labelName, nextVersionId);
    if (typeof response === "function") {
      return () => {
        response(refreshVersions);
        invalidateChangelogQueries();
      };
    }
    refreshVersions();
    invalidateChangelogQueries();
  };

  const onMoveReleaseLabel = useCallback(
    async (labelId: number, labelName: string, targetVersionId: number | string) => {
      const result = await workflow.updateReleaseLabel(labelId, Number(targetVersionId));
      refreshVersions();
      invalidateChangelogQueries();
      return result;
    },
    [invalidateChangelogQueries, refreshVersions, workflow],
  );

  const { data: workflowCounts } = useRequestWorflowCounts();

  return (
    <VersionSelector
      isWorkflow
      {...selectorProps}
      onAddReleaseLabel={onAddReleaseLabel}
      onMoveReleaseLabel={onMoveReleaseLabel}
      onRemoveReleaseLabel={onRemoveReleaseLabel}
      onArchiveWorkflowVersion={{
        mutate: archiveWorkflowVersionMutation.mutate,
        isPending: archiveWorkflowVersionMutation.isPending,
      }}
      refreshVersions={refreshVersions}
      counts={workflowCounts?.counters || {}}
    />
  );
};

export default WorkflowVersionSelector;
